import { useState, useEffect, useCallback } from 'react';
import { useRegion } from '../hooks/useRegion';
import { getBackendUrl } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { 
  Upload, 
  X, 
  Check, 
  ChevronLeft, 
  FileSpreadsheet,
  Loader2,
  CheckCircle2,
  XCircle,
  Package,
  Calendar,
  DollarSign,
  User,
  Plus,
  Split,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import FileDropzone from '@/components/FileDropzone';

export function XeroImporter({ isOpen, onClose, onImportComplete, equipment = [] }) {
  const { formatCurrency } = useRegion();
  
  // States
  const [step, setStep] = useState('upload'); // 'upload', 'matching', 'summary'
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [matches, setMatches] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [decisions, setDecisions] = useState({}); // { index: { confirmed: bool, equipment_id: string, splits: [...] } }
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState(null);
  
  // Multi-select split mode
  const [splitMode, setSplitMode] = useState(false);
  const [selectedSplits, setSelectedSplits] = useState({}); // { equipmentId: { selected: bool } }
  
  // Create new equipment states
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creatingEquipment, setCreatingEquipment] = useState(false);
  const [newEquipmentName, setNewEquipmentName] = useState('');
  const [newEquipmentCategory, setNewEquipmentCategory] = useState('');
  const [newEquipmentDailyRate, setNewEquipmentDailyRate] = useState('');
  const [newEquipmentPurchasePrice, setNewEquipmentPurchasePrice] = useState('');
  const [localEquipment, setLocalEquipment] = useState([]); // Track newly created equipment
  
  // Current match
  const currentMatch = matches[currentIndex];
  const progress = matches.length > 0 ? ((currentIndex + 1) / matches.length) * 100 : 0;
  const confirmedCount = Object.values(decisions).filter(d => d.confirmed).length;
  const skippedCount = Object.values(decisions).filter(d => !d.confirmed).length;

  // Get all equipment options for the list (defined early so it can be used in calculations)
  const getAllEquipmentOptions = () => {
    const options = [];
    
    // Best match first
    if (currentMatch?.equipment_id) {
      const matchEquip = equipment.find(e => e.id === currentMatch.equipment_id) || 
                         localEquipment.find(e => e.id === currentMatch.equipment_id);
      options.push({
        id: currentMatch.equipment_id,
        name: currentMatch.equipment_name,
        category: currentMatch.equipment_category,
        purchase_price: matchEquip?.purchase_price || 0,
        score: currentMatch.match_score,
        isBestMatch: true
      });
    }
    
    // Alternative matches
    if (currentMatch?.alternative_matches) {
      currentMatch.alternative_matches.forEach(alt => {
        if (!options.find(o => o.id === alt.id)) {
          const altEquip = equipment.find(e => e.id === alt.id) || 
                          localEquipment.find(e => e.id === alt.id);
          options.push({
            id: alt.id,
            name: alt.name,
            category: alt.category,
            purchase_price: altEquip?.purchase_price || 0,
            score: alt.score,
            isBestMatch: false
          });
        }
      });
    }
    
    // Newly created equipment
    localEquipment.forEach(e => {
      if (!options.find(o => o.id === e.id)) {
        options.push({
          id: e.id,
          name: e.name,
          category: e.category || 'Uncategorized',
          purchase_price: e.purchase_price || 0,
          score: null,
          isNew: true
        });
      }
    });
    
    // All other equipment
    equipment.forEach(e => {
      if (!options.find(o => o.id === e.id)) {
        options.push({
          id: e.id,
          name: e.name,
          category: e.category,
          purchase_price: e.purchase_price || 0,
          score: null,
          isBestMatch: false
        });
      }
    });
    
    return options;
  };

  // Calculate split amounts based on equipment purchase price
  const calculateSplitAmounts = () => {
    if (!currentMatch) return {};
    
    const totalAmount = currentMatch.line_item.line_amount;
    const selectedItems = Object.entries(selectedSplits).filter(([_, v]) => v.selected);
    
    // Get equipment data with purchase prices
    const allEquipmentOptions = getAllEquipmentOptions();
    const itemsWithValues = selectedItems.map(([id]) => {
      const equip = allEquipmentOptions.find(e => e.id === id);
      return {
        id,
        purchasePrice: equip?.purchase_price || 0
      };
    });
    
    const totalValue = itemsWithValues.reduce((sum, item) => sum + item.purchasePrice, 0);
    
    const amounts = {};
    itemsWithValues.forEach(item => {
      amounts[item.id] = totalValue > 0 ? (item.purchasePrice / totalValue) * totalAmount : 0;
    });
    
    return amounts;
  };

  const splitAmounts = calculateSplitAmounts();
  const selectedSplitCount = Object.values(selectedSplits).filter(v => v.selected).length;
  
  // Check if all selected equipment has purchase price
  const allEquipmentOptions = getAllEquipmentOptions();
  const selectedEquipmentMissingValue = splitMode && Object.entries(selectedSplits)
    .filter(([_, v]) => v.selected)
    .some(([id]) => {
      const equip = allEquipmentOptions.find(e => e.id === id);
      return !equip?.purchase_price || equip.purchase_price <= 0;
    });

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setStep('upload');
      setFile(null);
      setMatches([]);
      setCurrentIndex(0);
      setDecisions({});
      setImportResult(null);
      setSelectedEquipmentId(null);
      setSplitMode(false);
      setSelectedSplits({});
      setShowCreateForm(false);
      setNewEquipmentName('');
      setNewEquipmentCategory('');
      setNewEquipmentDailyRate('');
      setNewEquipmentPurchasePrice('');
      setLocalEquipment([]);
    }
  }, [isOpen]);

  // Update selected equipment when match changes
  useEffect(() => {
    if (currentMatch) {
      setSelectedEquipmentId(currentMatch.equipment_id || null);
      setSplitMode(false);
      setSelectedSplits({});
      setShowCreateForm(false);
      setNewEquipmentName('');
      setNewEquipmentCategory('');
      setNewEquipmentDailyRate('');
      setNewEquipmentPurchasePrice('');
    }
  }, [currentMatch]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!isOpen || step !== 'matching') return;

    const handleKeyDown = (e) => {
      // Prevent if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return;

      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          handleConfirm();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          handleSkip();
          break;
        case 'Escape':
          e.preventDefault();
          handleClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, step, currentMatch, currentIndex, selectedEquipmentId, splitMode, selectedSplits]);

  // File upload handler
  const handleFileChange = (selectedFile) => {
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error('Please upload a CSV file');
        return;
      }
      setFile(selectedFile);
    }
  };

  // Parse CSV
  const handleParse = async () => {
    if (!file) return;

    setParsing(true);
    try {
      // Get auth token
      const token = localStorage.getItem('session_token');
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }

      // Read file as text to avoid body lock issues with FormData in production
      const fileText = await file.text();
      
      // Send as JSON with authentication
      const response = await fetch(`${getBackendUrl()}/api/xero-import/parse`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          filename: file.name,
          content: fileText
        })
      });

      // Check response status first before reading body
      if (!response.ok) {
        let errorMessage = 'Failed to parse CSV';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch {
          // If response body can't be parsed, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // Read response once
      const responseData = await response.json();

      if (responseData.matches.length === 0) {
        toast.info('No equipment items found in this CSV');
        return;
      }

      setMatches(responseData.matches);
      toast.success(`Found ${responseData.matches.length} potential equipment items`);
      setStep('matching');
    } catch (error) {
      console.error('Parse error:', error);
      toast.error(error.message || 'Failed to parse CSV');
    } finally {
      setParsing(false);
    }
  };
  
  // Create new equipment
  const handleCreateEquipment = async () => {
    if (!newEquipmentName.trim()) {
      toast.error('Please enter an equipment name');
      return;
    }

    setCreatingEquipment(true);
    try {
      const token = localStorage.getItem('session_token');
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }

      const response = await fetch(`${getBackendUrl()}/api/equipment`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newEquipmentName.trim(),
          category: newEquipmentCategory.trim() || 'Uncategorized',
          daily_rate: parseFloat(newEquipmentDailyRate) || 0,
          purchase_price: parseFloat(newEquipmentPurchasePrice) || 0,
          description: `Created from Xero import: ${currentMatch?.line_item?.description || ''}`
        })
      });

      if (!response.ok) {
        let errorMessage = 'Failed to create equipment';
        try {
          const error = await response.json();
          errorMessage = error.detail || errorMessage;
        } catch {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const newEquip = await response.json();
      
      // Add to local equipment list
      setLocalEquipment(prev => [...prev, newEquip]);
      
      // Select the new equipment (either single or add to split)
      if (splitMode) {
        setSelectedSplits(prev => ({
          ...prev,
          [newEquip.id]: { selected: true }
        }));
      } else {
        setSelectedEquipmentId(newEquip.id);
      }
      
      // Hide the form
      setShowCreateForm(false);
      setNewEquipmentName('');
      setNewEquipmentCategory('');
      setNewEquipmentDailyRate('');
      setNewEquipmentPurchasePrice('');
      
      toast.success(`Created "${newEquip.name}"`);
    } catch (error) {
      console.error('Create equipment error:', error);
      toast.error(error.message || 'Failed to create equipment');
    } finally {
      setCreatingEquipment(false);
    }
  };

  // Open create form with pre-filled data
  const openCreateForm = () => {
    // Try to extract a clean name from the Xero description
    let suggestedName = currentMatch?.line_item?.description || '';
    // Remove common suffixes/extras
    suggestedName = suggestedName
      .replace(/\s*-\s*(camera hire|hire|rental).*$/i, '')
      .replace(/\s*\d+\s*(day|days|x).*$/i, '')
      .trim();
    
    setNewEquipmentName(suggestedName);
    setNewEquipmentCategory('');
    setNewEquipmentDailyRate(currentMatch?.line_item?.unit_amount?.toString() || '');
    setNewEquipmentPurchasePrice('');
    setShowCreateForm(true);
  };

  // Toggle split mode
  const toggleSplitMode = () => {
    if (!splitMode) {
      // Entering split mode - pre-select the keyword matches
      const newSplits = {};
      
      // First, include currently selected item if any
      if (selectedEquipmentId) {
        newSplits[selectedEquipmentId] = { selected: true };
      }
      
      if (currentMatch) {
        // Best match
        if (currentMatch.equipment_id && !newSplits[currentMatch.equipment_id]) {
          newSplits[currentMatch.equipment_id] = { selected: true };
        }
        
        // Alternative matches - any match with score > 0 means keyword match
        if (currentMatch.alternative_matches) {
          currentMatch.alternative_matches.forEach(alt => {
            if (alt.score > 0 && !newSplits[alt.id]) {
              newSplits[alt.id] = { selected: true };
            }
          });
        }
      }
      
      setSelectedSplits(newSplits);
    } else {
      // Exiting split mode - select first selected item as single
      const firstSelected = Object.entries(selectedSplits).find(([_, v]) => v.selected);
      setSelectedEquipmentId(firstSelected ? firstSelected[0] : null);
    }
    setSplitMode(!splitMode);
  };

  // Toggle equipment selection in split mode
  const toggleSplitSelection = (equipId) => {
    setSelectedSplits(prev => ({
      ...prev,
      [equipId]: {
        selected: !prev[equipId]?.selected
      }
    }));
  };

  // Confirm current match
  const handleConfirm = useCallback(() => {
    if (!currentMatch) return;

    if (splitMode) {
      // Multi-select mode
      const splits = Object.entries(selectedSplits)
        .filter(([_, v]) => v.selected)
        .map(([id]) => ({
          equipment_id: id,
          amount: splitAmounts[id] || 0
        }));

      if (splits.length === 0) {
        toast.error('Please select at least one equipment item');
        return;
      }

      // Validate all selected equipment has purchase price
      if (selectedEquipmentMissingValue) {
        toast.error('All selected equipment must have a Purchase Price (Equipment Value) to split income');
        return;
      }

      setDecisions(prev => ({
        ...prev,
        [currentIndex]: {
          confirmed: true,
          splits: splits
        }
      }));
    } else {
      // Single select mode
      if (!selectedEquipmentId) {
        toast.error('Please select equipment to match');
        return;
      }

      setDecisions(prev => ({
        ...prev,
        [currentIndex]: {
          confirmed: true,
          equipment_id: selectedEquipmentId
        }
      }));
    }

    // Move to next or finish
    if (currentIndex < matches.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setStep('summary');
    }
  }, [currentMatch, selectedEquipmentId, currentIndex, matches.length, splitMode, selectedSplits, splitAmounts]);

  // Skip current match
  const handleSkip = useCallback(() => {
    setDecisions(prev => ({
      ...prev,
      [currentIndex]: {
        confirmed: false
      }
    }));

    // Move to next or finish
    if (currentIndex < matches.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setStep('summary');
    }
  }, [currentIndex, matches.length]);

  // Go back
  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  // Final import
  const handleImport = async () => {
    const confirmations = [];
    
    Object.entries(decisions)
      .filter(([_, decision]) => decision.confirmed)
      .forEach(([index, decision]) => {
        const match = matches[parseInt(index)];
        
        if (decision.splits && decision.splits.length > 0) {
          // Multi-split - create one entry per split
          // Each split gets at least 1 day (round up, minimum 1)
          const daysPerSplit = Math.max(1, Math.round((match.line_item.quantity || 1) / decision.splits.length));
          decision.splits.forEach(split => {
            confirmations.push({
              equipment_id: split.equipment_id,
              description: match.line_item.description,
              income: split.amount,
              date: match.line_item.invoice_date,
              days: daysPerSplit,
              client_name: match.line_item.contact_name,
              invoice_number: match.line_item.invoice_number
            });
          });
        } else {
          // Single equipment
          confirmations.push({
            equipment_id: decision.equipment_id,
            description: match.line_item.description,
            income: match.line_item.line_amount,
            date: match.line_item.invoice_date,
            days: match.line_item.quantity || 1,
            client_name: match.line_item.contact_name,
            invoice_number: match.line_item.invoice_number
          });
        }
      });

    if (confirmations.length === 0) {
      toast.info('No items to import');
      onClose();
      return;
    }

    setImporting(true);
    try {
      const token = localStorage.getItem('session_token');
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }

      const response = await fetch(`${getBackendUrl()}/api/xero-import/confirm`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(confirmations)
      });

      if (!response.ok) {
        let errorMessage = 'Failed to import';
        try {
          const error = await response.json();
          errorMessage = error.detail || errorMessage;
        } catch {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      setImportResult(result);
      toast.success(`Imported ${result.imported_count} historical entries`);
      
      if (onImportComplete) {
        onImportComplete();
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.message || 'Failed to import');
    } finally {
      setImporting(false);
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // Handle DD/MM/YYYY format
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return new Date(year, month - 1, day).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    }
    return dateStr;
  };

  // Get confidence badge color
  const getConfidenceBadge = (score) => {
    if (score >= 0.8) return { label: 'High', className: 'border-transparent bg-moss-soft text-moss' };
    if (score >= 0.5) return { label: 'Good', className: 'border-transparent bg-ochre-soft text-ochre' };
    if (score >= 0.3) return { label: 'Possible', className: 'border-transparent bg-ochre-soft text-ochre' };
    return { label: 'Low', className: 'border-transparent bg-brick-soft text-brick' };
  };

  if (!isOpen) return null;

  // Handle close with warning during matching
  const handleClose = () => {
    if (step === 'matching') {
      const hasDecisions = Object.keys(decisions).length > 0;
      if (hasDecisions) {
        const confirmed = window.confirm(
          'Your matching progress will not be saved unless you complete the import.\n\n' +
          'Are you sure you want to close?'
        );
        if (!confirmed) return;
      }
    }
    onClose();
  };

  // Determine if clicking backdrop should close the modal
  // During matching step, we want to prevent accidental closure
  const handleBackdropClick = () => {
    if (step === 'matching') {
      // Don't close during matching - user might accidentally click outside
      toast.info('Please complete matching or use the X button to close');
      return;
    }
    onClose();
  };

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 z-50 flex items-center justify-center py-8 px-4">
      {/* Backdrop */}
      <div 
        className="fixed top-0 left-0 right-0 bottom-0 bg-black/80"
        onClick={handleBackdropClick}
      />
      
      {/* Modal - 80% viewport width */}
      <div className="relative w-full max-w-[80vw] bg-parchment rounded-highlight shadow-modal overflow-hidden flex flex-col" style={{ maxHeight: 'calc(100vh - 4rem)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-line shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-pill bg-mist">
              <FileSpreadsheet className="h-[18px] w-[18px] text-foreground" />
            </div>
            <div>
              <h2 className="text-2xl font-normal leading-tight">Xero importer</h2>
              <p className="text-sm text-muted-foreground">
                {step === 'upload' && 'Upload your Xero sales invoice CSV'}
                {step === 'matching' && `Matching item ${currentIndex + 1} of ${matches.length}`}
                {step === 'summary' && 'Import summary'}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={handleClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Progress bar (for matching step) */}
        {step === 'matching' && (
          <div className="px-6 pt-4 shrink-0">
            <Progress value={progress} className="h-2" />
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
              <span>{confirmedCount} confirmed</span>
              <span>{skippedCount} skipped</span>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6 flex-1 min-h-0 overflow-hidden flex flex-col">
          {/* Upload Step */}
          {step === 'upload' && (
            <div className="space-y-6 max-w-xl mx-auto">
              <FileDropzone
                accept=".csv"
                onSelect={handleFileChange}
                file={file}
                title="Choose your Xero CSV"
              />

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button onClick={handleParse} disabled={!file || parsing}>
                  {parsing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Parsing...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      Parse CSV
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Matching Step - Split Panel Layout */}
          {step === 'matching' && currentMatch && (
            <div className="flex gap-6 flex-1 min-h-0">
              {/* Left Side - Xero Line Item */}
              <div className="w-1/2 flex flex-col min-h-0 overflow-hidden">
                <p className="text-sm font-medium mb-3 shrink-0">Xero line item</p>
                
                {/* Scrollable container for entire left column content */}
                <div className="flex-1 overflow-y-auto min-h-0 pr-1">
                  {/* Xero Line Item Details */}
                  <div className="p-4 rounded-tile bg-surface space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium text-base leading-tight flex-1 whitespace-pre-line break-words">
                        {currentMatch.line_item.description}
                      </p>
                      <Badge variant="outline" className="shrink-0 text-xs">
                        {currentMatch.line_item.invoice_number}
                      </Badge>
                    </div>
                  
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2 text-sm">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{formatCurrency(currentMatch.line_item.line_amount)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span>{formatDate(currentMatch.line_item.invoice_date)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm col-span-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span>{currentMatch.line_item.contact_name || 'Unknown client'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Split Mode Toggle */}
                  <div className="mt-4">
                    <Button
                      variant={splitMode ? "default" : "outline"}
                      onClick={toggleSplitMode}
                      className="w-full"
                      size="sm"
                    >
                      <Split className="h-4 w-4" />
                      {splitMode ? "Split mode active" : "Split across multiple items"}
                    </Button>
                  </div>

                  {/* Split Summary */}
                  {splitMode && selectedSplitCount > 0 && (
                    <div className="mt-3 p-3 rounded-cards bg-surface text-sm">
                      <p className="font-medium mb-2">Split preview (by equipment value)</p>
                      <div className="space-y-1 text-xs">
                        {Object.entries(selectedSplits)
                          .filter(([_, v]) => v.selected)
                          .map(([id]) => {
                            const equip = getAllEquipmentOptions().find(e => e.id === id);
                            const hasMissingValue = !equip?.purchase_price || equip.purchase_price <= 0;
                            return (
                              <div key={id} className="flex justify-between">
                                <span className="truncate mr-2 flex items-center gap-1">
                                  {equip?.name || 'Unknown'}
                                  {hasMissingValue && (
                                    <AlertTriangle className="w-3 h-3 text-brick shrink-0" />
                                  )}
                                </span>
                                <span className={hasMissingValue ? "text-brick" : "font-medium"}>
                                  {hasMissingValue ? 'No value' : formatCurrency(splitAmounts[id] || 0)}
                                </span>
                              </div>
                            );
                          })}
                        <div className="flex justify-between pt-1 border-t border-line mt-1 font-medium">
                          <span>Total</span>
                          <span>{formatCurrency(currentMatch.line_item.line_amount)}</span>
                        </div>
                      </div>
                      {selectedEquipmentMissingValue && (
                        <p className="text-xs text-brick mt-2 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3 shrink-0" /> All equipment must have a purchase price to split income
                        </p>
                      )}
                    </div>
                  )}

                  {/* Create New Equipment Section */}
                  <div className="mt-4">
                    {!showCreateForm ? (
                      <Button
                        variant="outline"
                        onClick={openCreateForm}
                        className="w-full border-dashed"
                      >
                        <Plus className="h-4 w-4" />
                        Create new equipment
                      </Button>
                    ) : (
                      <div className="p-4 rounded-tile bg-surface space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium">Create new equipment</p>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => setShowCreateForm(false)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="new-equip-name" className="text-xs">Name *</Label>
                          <Input
                            id="new-equip-name"
                            value={newEquipmentName}
                            onChange={(e) => setNewEquipmentName(e.target.value)}
                            placeholder="Equipment name"
                            className="h-9"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="new-equip-category" className="text-xs">Category</Label>
                          <Input
                            id="new-equip-category"
                            value={newEquipmentCategory}
                            onChange={(e) => setNewEquipmentCategory(e.target.value)}
                            placeholder="e.g., Camera"
                            className="h-9"
                          />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label htmlFor="new-equip-rate" className="text-xs">Daily rate</Label>
                            <Input
                              id="new-equip-rate"
                              type="number"
                              value={newEquipmentDailyRate}
                              onChange={(e) => setNewEquipmentDailyRate(e.target.value)}
                              placeholder="0.00"
                              className="h-9"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="new-equip-purchase-price" className="text-xs">Purchase price *</Label>
                            <Input
                              id="new-equip-purchase-price"
                              type="number"
                              value={newEquipmentPurchasePrice}
                              onChange={(e) => setNewEquipmentPurchasePrice(e.target.value)}
                              placeholder="0.00"
                              className="h-9"
                            />
                          </div>
                        </div>
                        
                        <Button
                          onClick={handleCreateEquipment}
                          disabled={creatingEquipment || !newEquipmentName.trim()}
                          className="w-full"
                          size="sm"
                        >
                          {creatingEquipment ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Creating...
                            </>
                          ) : (
                            <>
                              <Check className="h-4 w-4" />
                              Create & select
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Keyboard shortcuts hint - fixed at bottom */}
                <div className="shrink-0 pt-3 border-t border-line mt-3">
                  <div className="flex items-center justify-center gap-6 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <kbd className="px-2 py-0.5 rounded-small bg-mist text-[11px] text-foreground">←</kbd>
                      <span>Skip</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <kbd className="px-2 py-0.5 rounded-small bg-mist text-[11px] text-foreground">→</kbd>
                      <span>Confirm</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <kbd className="px-2 py-0.5 rounded-small bg-mist text-[11px] text-foreground">Esc</kbd>
                      <span>Close</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side - Equipment List */}
              <div className="w-1/2 flex flex-col min-h-0 overflow-hidden">
                <p className="text-sm font-medium mb-3 shrink-0">
                  {splitMode ? "Select equipment items (split by value)" : "Select equipment match"}
                </p>
                
                <div className="flex-1 overflow-y-auto bg-surface rounded-cards min-h-0">
                  {/* Sort equipment: best match, then by score, then alphabetical */}
                  {[...getAllEquipmentOptions()]
                    .sort((a, b) => {
                      // Best match first
                      if (a.isBestMatch && !b.isBestMatch) return -1;
                      if (!a.isBestMatch && b.isBestMatch) return 1;
                      // Then by score
                      if ((a.score || 0) !== (b.score || 0)) {
                        return (b.score || 0) - (a.score || 0);
                      }
                      // Finally alphabetical
                      return a.name.localeCompare(b.name);
                    })
                    .map((option) => {
                    const isSelected = splitMode 
                      ? selectedSplits[option.id]?.selected 
                      : selectedEquipmentId === option.id;
                    const hasMissingValue = splitMode && (!option.purchase_price || option.purchase_price <= 0);
                    
                    return (
                      <div
                        key={option.id}
                        className={cn(
                          "flex items-center gap-3 p-3 border-b border-line last:border-b-0 transition-colors",
                          isSelected 
                            ? "bg-mist border-l-2 border-l-foreground" 
                            : "hover:bg-mist/60",
                          !splitMode && "cursor-pointer"
                        )}
                        onClick={() => !splitMode && setSelectedEquipmentId(option.id)}
                      >
                        {/* Checkbox for split mode */}
                        {splitMode && (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSplitSelection(option.id)}
                            className="shrink-0"
                          />
                        )}
                        
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <Package className={cn(
                            "h-4 w-4 shrink-0",
                            option.isNew ? "text-moss" : 
                            option.isBestMatch ? "text-foreground" : 
                            "text-ink-muted"
                          )} />
                          <div className="min-w-0 flex-1">
                            <p className={cn(
                              "font-medium truncate",
                              isSelected && "text-primary"
                            )}>
                              {option.name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {option.category}
                              {splitMode && (
                                <span className={cn(
                                  "ml-2",
                                  hasMissingValue && "text-brick font-medium"
                                )}>
                                  • Value: {option.purchase_price > 0 ? formatCurrency(option.purchase_price) : <>None <AlertTriangle className="inline w-3 h-3 -mt-0.5" /></>}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        
                        {/* Calculated amount for split mode */}
                        {splitMode && isSelected && (
                          <div className={cn(
                            "shrink-0 text-sm font-medium",
                            hasMissingValue ? "text-brick" : "text-foreground"
                          )}>
                            {hasMissingValue ? 'No value' : formatCurrency(splitAmounts[option.id] || 0)}
                          </div>
                        )}
                        
                        {/* Badges */}
                        <div className="flex items-center gap-2 shrink-0">
                          {!splitMode && (
                            <>
                              {option.isNew && (
                                <Badge className="border-transparent bg-moss-soft text-moss text-xs">
                                  New
                                </Badge>
                              )}
                              {option.isBestMatch && (
                                <Badge className="text-xs">
                                  Best match
                                </Badge>
                              )}
                              {isSelected && (
                                <CheckCircle2 className="h-[18px] w-[18px] text-moss" />
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Summary Step */}
          {step === 'summary' && (
            <div className="space-y-6 max-w-2xl mx-auto">
              {importResult ? (
                // Post-import summary
                <div className="text-center py-8">
                  <CheckCircle2 className="h-16 w-16 mx-auto text-moss mb-4" />
                  <h3 className="section-title mb-2">Import complete!</h3>
                  <p className="text-muted-foreground">
                    Successfully imported {importResult.imported_count} historical entries
                  </p>
                  {importResult.errors?.length > 0 && (
                    <div className="mt-4 p-3 bg-brick-soft rounded-cards text-sm text-brick">
                      {importResult.errors.length} errors occurred
                    </div>
                  )}
                  <Button onClick={handleClose} className="mt-6">
                    Done
                  </Button>
                </div>
              ) : (
                // Pre-import summary
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-tile bg-surface text-center">
                      <CheckCircle2 className="h-8 w-8 mx-auto text-moss mb-2" />
                      <p className="text-4xl font-light tracking-tight text-moss">{confirmedCount}</p>
                      <p className="text-sm text-muted-foreground">Items to import</p>
                    </div>
                    <div className="p-4 rounded-tile bg-surface text-center">
                      <XCircle className="h-8 w-8 mx-auto text-ink-faded mb-2" />
                      <p className="text-4xl font-light tracking-tight">{skippedCount}</p>
                      <p className="text-sm text-muted-foreground">Items skipped</p>
                    </div>
                  </div>

                  {confirmedCount > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Items to be imported:</p>
                      <div className="max-h-48 overflow-y-auto space-y-2">
                        {Object.entries(decisions)
                          .filter(([_, d]) => d.confirmed)
                          .map(([index, decision]) => {
                            const match = matches[parseInt(index)];
                            return (
                              <div 
                                key={index}
                                className="p-2 rounded-small bg-surface text-sm"
                              >
                                <div className="flex items-center justify-between">
                                  <span className="truncate flex-1 mr-2">
                                    {match.line_item.description}
                                  </span>
                                  <span className="font-medium shrink-0">
                                    {formatCurrency(match.line_item.line_amount)}
                                  </span>
                                </div>
                                {decision.splits && decision.splits.length > 1 && (
                                  <div className="mt-1 pl-2 text-xs text-muted-foreground">
                                    <Split className="h-3 w-3 inline mr-1" />
                                    Split across {decision.splits.length} items
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-4 border-t border-line">
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setCurrentIndex(0);
                        setStep('matching');
                      }}
                    >
                      Review again
                    </Button>
                    <Button 
                      onClick={handleImport}
                      disabled={confirmedCount === 0 || importing}
                    >
                      {importing ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4" />
                          Import {confirmedCount} items
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer with action buttons (for matching step) */}
        {step === 'matching' && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-line shrink-0">
            <Button 
              variant="ghost" 
              onClick={handleBack}
              disabled={currentIndex === 0}
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleSkip}
                className="min-w-[100px]"
              >
                <X className="h-4 w-4" />
                Skip
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={splitMode ? (selectedSplitCount === 0 || selectedEquipmentMissingValue) : !selectedEquipmentId}
                className="min-w-[100px]"
              >
                <Check className="h-4 w-4" />
                Confirm{splitMode && selectedSplitCount > 0 ? ` (${selectedSplitCount})` : ''}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
